import type * as React from 'react';

import { type Href, type RouterOptions, useRouterContext } from './Router';

/**
 * Check if the href should be handled by navigate function or not.
 */
function checkIsNaviguableHref(href: string) {
    return href && !href.startsWith('http') && !href.startsWith('//');
}

export type NavigationHookProps = {
    href?: Href;
    target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
    download?: React.AnchorHTMLAttributes<HTMLAnchorElement>['download'];
    routerOptions?: RouterOptions;
    onClick?: React.MouseEventHandler<HTMLElement>;
};

export type NavigationResult = {
    href?: string | undefined;
    onClick?: React.MouseEventHandler<HTMLElement>;
};

/**
 * Returns props for a button to enable navigation through the client-side router.
 */
export function useNavigationProps(props: NavigationHookProps): NavigationResult {
    const router = useRouterContext();

    // If there is no router, we don't need to do anything
    // About rules of hook, yes it breaks them but only if
    // we have a dynamic `RouterProvider` in the tree or if we have a dynamic `href`.
    // By dynamic I mean having one, then not having one, then having one again.
    // And we will intentionally not support that.
    if (!router || props.href === undefined) {
        return { onClick: props.onClick, href: props.href as unknown as string | undefined };
    }

    // biome-ignore lint/correctness/useHookAtTopLevel: See above
    const href = router.useHref(props.href);

    return {
        href,
        /*
         * If the button is a link (with href) we need to allow
         * browser default behaviour when meta or ctrl key is pressed
         * or when forcing a hard navigation otherwise we prevent
         * default behaviour to allow router to navigate in app.
         */
        onClick: (event) => {
            props.onClick?.(event);

            // Let the browser handle the click if:
            // - the href is not navigable
            // - the target is not "_self"
            // - the download attribute is set
            // - the event is already prevented
            // - the meta, ctrl or alt key is pressed
            if (
                !props.href ||
                !checkIsNaviguableHref(href) ||
                (props.target && props.target !== 'self') ||
                props.download ||
                event.isDefaultPrevented() ||
                event.metaKey ||
                event.ctrlKey ||
                event.altKey
            ) {
                return;
            }

            event.preventDefault();
            router.navigate(props.href, props.routerOptions);
        },
    };
}
